/* Empty state shown on multiple pages when no sources exist yet.
   Displays a 2-column grid of content-type tiles the user can click to get started.
   Podcast and RSS tiles include self-contained OPML import flows. */

import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Youtube, Rss, Podcast, Mail, ChevronDown, Check, Compass, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { RoutePath } from "@/routePaths";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  ApplePodcastsIcon,
  OvercastIcon,
  CastroIcon,
  PocketCastsIcon,
} from "@/components/sources/PodcastAppIcons";
import {
  FeedlyIcon,
  InoreaderIcon,
  ReederIcon,
  NewsBlurIcon,
  NetNewsWireIcon,
} from "@/components/sources/RssReaderIcons";
import { FeedImportDialog } from "@/components/sources/PodcastImportDialog";
import { YouTubeImportDialog } from "@/components/youtube/YouTubeImportDialog";
import { useYouTubeImport } from "@/hooks/useYouTubeImport";

export type ImportTileType =
  | "youtube"
  | "podcast"
  | "rss"
  | "newsletter";

export type PodcastApp =
  | "apple-podcasts"
  | "overcast"
  | "castro"
  | "pocket-casts"
  | "rss";

export type RssReaderApp =
  | "feedly"
  | "inoreader"
  | "reeder"
  | "newsblur"
  | "netnewswire"
  | "rss-url";

interface AppOption {
  id: string;
  label: string;
  icon: React.ElementType;
}

const PODCAST_APPS: AppOption[] = [
  { id: "apple-podcasts", label: "Apple Podcasts", icon: ApplePodcastsIcon },
  { id: "overcast", label: "Overcast", icon: OvercastIcon },
  { id: "castro", label: "Castro", icon: CastroIcon },
  { id: "pocket-casts", label: "Pocket Casts", icon: PocketCastsIcon },
  { id: "rss", label: "RSS Feed URL", icon: Rss },
];

const RSS_READER_APPS: AppOption[] = [
  { id: "feedly", label: "Feedly", icon: FeedlyIcon },
  { id: "inoreader", label: "Inoreader", icon: InoreaderIcon },
  { id: "reeder", label: "Reeder", icon: ReederIcon },
  { id: "newsblur", label: "NewsBlur", icon: NewsBlurIcon },
  { id: "netnewswire", label: "NetNewsWire", icon: NetNewsWireIcon },
  { id: "rss-url", label: "RSS Feed URL", icon: Rss },
];

interface ImportTile {
  type: ImportTileType;
  icon: React.ElementType;
  title: string;
  description: string;
  completedLabel: string;
}

const IMPORT_TILES: ImportTile[] = [
  {
    type: "youtube",
    icon: Youtube,
    title: "YouTube",
    description: "Subscribe to channels",
    completedLabel: "Channels imported",
  },
  {
    type: "podcast",
    icon: Podcast,
    title: "Podcasts",
    description: "Import from your app",
    completedLabel: "Podcasts imported",
  },
  {
    type: "rss",
    icon: Rss,
    title: "RSS / Blogs",
    description: "Import from your reader",
    completedLabel: "Feeds added",
  },
  {
    type: "newsletter",
    icon: Mail,
    title: "Newsletters",
    description: "Connect your inbox",
    completedLabel: "Inbox connected",
  },
];

export type NoSourcesVariant = "cards" | "minimal";

interface NoSourcesEmptyStateProps {
  variant?: NoSourcesVariant;
  completedTypes?: ImportTileType[];
  onTileClick?: (type: ImportTileType) => void;
  onPodcastAppClick?: (app: PodcastApp) => void;
  youTubeDialogOpen?: boolean;
  onYouTubeDialogChange?: (open: boolean) => void;
  onDiscoverClick?: () => void;
}

export function NoSourcesEmptyState({
  variant = "cards",
  completedTypes = [],
  onTileClick,
  onPodcastAppClick,
  youTubeDialogOpen: externalYtOpen,
  onYouTubeDialogChange: externalYtOnChange,
  onDiscoverClick,
}: NoSourcesEmptyStateProps) {
  const navigate = useNavigate();
  const completedSet = new Set(completedTypes);

  // YouTube import state
  const [internalYtOpen, setInternalYtOpen] = useState(false);
  const ytOpen = externalYtOpen ?? internalYtOpen;
  const setYtOpen = externalYtOnChange ?? setInternalYtOpen;
  const youtubeImport = useYouTubeImport();

  // Podcast import state
  const [podcastApp, setPodcastApp] = useState<string | null>(null);
  const handlePodcastAppClick = useCallback(
    (app: string) => {
      onPodcastAppClick?.(app as PodcastApp);
      setPodcastApp(app);
    },
    [onPodcastAppClick],
  );

  // RSS reader import state
  const [rssApp, setRssApp] = useState<string | null>(null);
  const handleRssAppClick = useCallback((app: string) => {
    setRssApp(app);
  }, []);

  return (
    <div className="flex flex-col items-center gap-6 px-4 py-10 max-w-md mx-auto">
      <div className="text-center space-y-1">
        <h2 className="text-lg font-semibold text-heading-foreground">
          Add your own sources
        </h2>
        <p className="text-sm text-muted-foreground">
          Import content from apps you already use
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 w-full">
        {IMPORT_TILES.map((tile) => {
          if (tile.type === "youtube") {
            return (
              <ImportTileButton
                key={tile.type}
                tile={tile}
                variant={variant}
                completed={completedSet.has(tile.type)}
                onClick={() => {
                  if (youtubeImport.hasConnection) {
                    youtubeImport.fetchChannels();
                  }
                  setYtOpen(true);
                }}
              />
            );
          }
          if (tile.type === "podcast") {
            return (
              <PopoverImportTile
                key={tile.type}
                tile={tile}
                variant={variant}
                completed={completedSet.has(tile.type)}
                apps={PODCAST_APPS}
                onAppClick={handlePodcastAppClick}
              />
            );
          }
          if (tile.type === "rss") {
            return (
              <PopoverImportTile
                key={tile.type}
                tile={tile}
                variant={variant}
                completed={completedSet.has(tile.type)}
                apps={RSS_READER_APPS}
                onAppClick={handleRssAppClick}
              />
            );
          }
          return (
            <ImportTileButton
              key={tile.type}
              tile={tile}
              variant={variant}
              completed={completedSet.has(tile.type)}
              onClick={() => onTileClick?.(tile.type)}
            />
          );
        })}
      </div>

      <div className="flex items-center gap-3 w-full">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground font-medium whitespace-nowrap">
          or discover suggestions
        </span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <button
        type="button"
        onClick={onDiscoverClick ?? (() => navigate(RoutePath.Sources))}
        className="group w-full rounded-xl border border-dashed border-primary/30 bg-primary/[0.03] p-6 text-left cursor-pointer transition-all hover:border-primary/50 hover:bg-primary/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <div className="flex items-start gap-4">
          <div className="flex items-center justify-center size-12 rounded-xl bg-primary/10 shrink-0">
            <Compass className="size-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-semibold text-foreground">
              Discover new sources
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Tell us what you're interested in and we'll suggest channels,
              podcasts, blogs, and newsletters tailored to you.
            </p>
          </div>
          <ArrowRight className="size-5 text-muted-foreground shrink-0 mt-0.5 transition-transform group-hover:translate-x-0.5" />
        </div>
      </button>

      <YouTubeImportDialog
        open={ytOpen}
        onOpenChange={setYtOpen}
        importState={youtubeImport}
      />
      <FeedImportDialog
        app={podcastApp}
        sourceType="podcast"
        onOpenChange={(open) => { if (!open) setPodcastApp(null); }}
      />
      <FeedImportDialog
        app={rssApp}
        sourceType="rss"
        onOpenChange={(open) => { if (!open) setRssApp(null); }}
      />
    </div>
  );
}

/* ── Completed badge (shared) ── */

function CompletedBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <Check className="size-3" />
      {label}
    </span>
  );
}

/* ── Standard tile (no popover) ── */

interface ImportTileButtonProps {
  tile: ImportTile;
  variant: NoSourcesVariant;
  completed: boolean;
  onClick: () => void;
}

function ImportTileButton({ tile, variant, completed, onClick }: ImportTileButtonProps) {
  const Icon = tile.icon;

  if (variant === "minimal") {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "flex items-center gap-3 rounded-xl px-4 py-3",
          "border bg-card text-left cursor-pointer",
          "transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          completed
            ? "border-border/60 opacity-50 hover:opacity-80 hover:bg-accent hover:border-accent"
            : "border-border hover:bg-accent hover:border-accent",
        )}
      >
        <Icon className="size-5 shrink-0 text-muted-foreground" />
        <div className="flex flex-col min-w-0">
          <span className="text-sm font-medium truncate">{tile.title}</span>
          {completed && <CompletedBadge label={tile.completedLabel} />}
        </div>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex flex-col items-start gap-3 rounded-xl p-4",
        "border bg-card text-left cursor-pointer",
        "transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        completed
          ? "border-border/60 opacity-50 hover:opacity-80 hover:bg-accent hover:border-accent"
          : "border-border hover:bg-accent hover:border-accent",
      )}
    >
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center justify-center size-9 rounded-lg bg-muted">
          <Icon className="size-4.5 text-muted-foreground" />
        </div>
        {completed && (
          <Check className="size-4 text-muted-foreground" />
        )}
      </div>
      <div className="space-y-0.5">
        <p className="text-sm font-medium leading-tight">{tile.title}</p>
        <p className="text-xs text-muted-foreground leading-snug">
          {completed ? tile.completedLabel : tile.description}
        </p>
      </div>
    </button>
  );
}

/* ── Tile with popover listing apps (used for both Podcasts and RSS) ── */

interface PopoverImportTileProps {
  tile: ImportTile;
  variant: NoSourcesVariant;
  completed: boolean;
  apps: AppOption[];
  onAppClick: (appId: string) => void;
}

function PopoverImportTile({
  tile,
  variant,
  completed,
  apps,
  onAppClick,
}: PopoverImportTileProps) {
  const Icon = tile.icon;
  const [popoverOpen, setPopoverOpen] = useState(false);

  const handleAppClick = useCallback(
    (appId: string) => {
      setPopoverOpen(false);
      onAppClick(appId);
    },
    [onAppClick],
  );

  if (variant === "minimal") {
    return (
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex items-center gap-3 rounded-xl px-4 py-3",
              "border bg-card text-left cursor-pointer",
              "transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              completed
                ? "border-border/60 opacity-50 hover:opacity-80 hover:bg-accent hover:border-accent"
                : "border-border hover:bg-accent hover:border-accent",
            )}
          >
            <Icon className="size-5 shrink-0 text-muted-foreground" />
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-sm font-medium truncate">{tile.title}</span>
              {completed && <CompletedBadge label={tile.completedLabel} />}
            </div>
            <ChevronDown className="size-3.5 text-muted-foreground shrink-0" />
          </button>
        </PopoverTrigger>
        <AppListPopoverContent apps={apps} onAppClick={handleAppClick} />
      </Popover>
    );
  }

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex flex-col items-start gap-3 rounded-xl p-4",
            "border bg-card text-left cursor-pointer",
            "transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            completed
              ? "border-border/60 opacity-50 hover:opacity-80 hover:bg-accent hover:border-accent"
              : "border-border hover:bg-accent hover:border-accent",
          )}
        >
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center justify-center size-9 rounded-lg bg-muted">
              <Icon className="size-4.5 text-muted-foreground" />
            </div>
            <div className="flex items-center gap-1">
              {completed && <Check className="size-4 text-muted-foreground" />}
              <ChevronDown className="size-3.5 text-muted-foreground" />
            </div>
          </div>
          <div className="space-y-0.5">
            <p className="text-sm font-medium leading-tight">{tile.title}</p>
            <p className="text-xs text-muted-foreground leading-snug">
              {completed ? tile.completedLabel : tile.description}
            </p>
          </div>
        </button>
      </PopoverTrigger>
      <AppListPopoverContent apps={apps} onAppClick={handleAppClick} />
    </Popover>
  );
}

/* ── Popover content listing apps ── */

interface AppListPopoverContentProps {
  apps: AppOption[];
  onAppClick: (appId: string) => void;
}

function AppListPopoverContent({ apps, onAppClick }: AppListPopoverContentProps) {
  return (
    <PopoverContent align="start" className="w-56 p-1">
      <p className="px-2.5 py-1.5 text-xs font-medium text-muted-foreground">
        Import from
      </p>
      {apps.map((app) => {
        const AppIcon = app.icon;
        return (
          <button
            key={app.id}
            type="button"
            onClick={() => onAppClick(app.id)}
            className={cn(
              "flex items-center gap-2.5 w-full rounded-md px-2.5 py-2 text-sm text-left",
              "transition-colors hover:bg-accent",
              "focus-visible:outline-none focus-visible:bg-accent",
            )}
          >
            <AppIcon className="size-4 shrink-0 text-muted-foreground" />
            {app.label}
          </button>
        );
      })}
    </PopoverContent>
  );
}
